/**
 * Type definitions for the Report Wizard state and configuration.
 */

import type { FilterConditionGroup } from '@/lib/crm/filters/types';
import type { ChartConfiguration } from '@/pages/reports/types';
import type { ChartType } from '@/types/reports';
import { ENTITY_TYPE_DISPLAY_NAMES } from '@/types/reports';

/**
 * Configuration for the data source selection step
 */
export interface DataSourceConfig {
  /** Primary CRM object type (entity_type_name) */
  primaryObjectType: string | null;
  /** Related object types to include in the query */
  relatedObjectTypes: string[];
}

/**
 * Measure selection with aggregation
 */
export interface MeasureSelection {
  /** Property identifier. Omitted for a propertyless 'count' that counts object instances; required for 'sum'/'avg'. */
  property?: string;
  /** Aggregation function: count, sum, avg */
  aggregation: 'count' | 'sum' | 'avg';
}

/**
 * Returns true when the measure is a propertyless count (counts object instances).
 * Accepts a Pick so callers working with partial shapes can use it without widening the type.
 */
export function isPropertylessCountMeasure(
  measure: Pick<MeasureSelection, 'aggregation' | 'property'>
): boolean {
  return measure.aggregation === 'count' && !measure.property;
}

/**
 * Normalizes count measures so they are always propertyless and never
 * duplicated: every `count` measure has its `property` dropped (a `count`
 * counts object instances and cannot carry a property), and since that
 * makes all count measures identical, only the first is kept. Non-count
 * measures (sum/avg) pass through unchanged, preserving order.
 */
export function normalizeCountMeasures(
  measures: MeasureSelection[]
): MeasureSelection[] {
  let seenCount = false;
  const result: MeasureSelection[] = [];
  for (const measure of measures) {
    if (measure.aggregation === 'count') {
      if (seenCount) {
        continue;
      }
      seenCount = true;
      result.push({ aggregation: 'count' });
    } else {
      result.push(measure);
    }
  }
  return result;
}

/**
 * Display label for the propertyless count measure — "Count of Contacts".
 * Mirrors the backend's aggregation column label (entity plural), so the
 * measure well, sort options, and executed table columns all agree.
 *
 * `pluralLabel` is the caller-resolved plural display name (see
 * reportSourceDisplayName) — required so custom object types label with
 * their schema plural instead of leaking the raw entity type name.
 */
export function propertylessCountLabel(
  primaryObjectType: string | null,
  pluralLabel: string | null
): string {
  const name =
    pluralLabel ??
    (primaryObjectType
      ? (ENTITY_TYPE_DISPLAY_NAMES[primaryObjectType] ?? primaryObjectType)
      : 'records');
  return `Count of ${name}`;
}

/**
 * Group by selection with optional time granularity
 */
export interface GroupBySelection {
  /** Property identifier (e.g., dimension name). May be empty string when grouping by an association instead. */
  property: string;
  /** Association type name to group by (mutually exclusive with property-based groupBy). When set, the wizard treats this entry as an association group_by and emits {association: name} in native queries. */
  association?: string;
  /** Time granularity for time-based dimensions */
  timeGranularity?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  /** Date range for time-based dimensions */
  dateRange?: string;
}

/**
 * Configuration for the fields selection step
 */
export interface FieldsConfig {
  /** Selected measures with aggregations */
  measures: MeasureSelection[];
  /** Selected group-by dimensions */
  groupBy: GroupBySelection[];
}

/**
 * Configuration for the customization step
 */
export interface CustomizeConfig {
  /** Group-native filter conditions: OR between groups, AND within each group */
  filters: FilterConditionGroup[];
  /**
   * Legacy input only. Present on older stored/legacy states where filters
   * were a flat FilterCondition[]; normalized on read by
   * `toFilterConditionGroups` (see `../utils/filterConfigMigration`). Not
   * emitted by group-native authoring.
   */
  filterLogic?: 'AND' | 'OR';
  /** Chart type identifier */
  chartType: ChartType | '';
  /** Sort order configuration */
  orderBy: {
    field: string;
    direction: 'asc' | 'desc';
  } | null;
  /** Result limit */
  limit: number | null;
  /**
   * Viewer-adjustable filters: association dimensions the author exposes so
   * viewers can filter the report from the view surface (dashboard tile
   * focus mode) without opening the editor. Persisted as `view_filters` on
   * the native query.
   */
  viewFilters?: ViewFilterSelection[];
  /**
   * Persisted partial chart config; the layout control writes
   * multiMeasureLayout. Round-trips to/from ahoy_chart_options.
   */
  chartConfig?: Partial<ChartConfiguration>;
}

/**
 * Editor-state twin of the persisted ReportViewFilter (camelCase). Exactly
 * one of `association` / `property` is set: association entries render an
 * entity picker; property entries name a date/datetime property and render a
 * time-range picker (selection encoded 'start..end').
 */
export interface ViewFilterSelection {
  /** Association type name (e.g. 'ahoy_deal_to_deal_pipeline'). */
  association?: string;
  /** Date/datetime property name (e.g. 'ahoy_close_date'). */
  property?: string;
  /** Control label shown to viewers (e.g. 'Pipeline', 'Close Date'). */
  label: string;
  /** Default selection (entity id, or 'start..end' range); null = All. */
  defaultValue: string | null;
}

/**
 * Complete editor state — the derived, in-memory representation the report
 * editor works on. Derived by parsing the persisted NativeReportQuery (or a
 * legacy wizard_state blob) on open; never persisted itself. There is no
 * step field: the editor is a single surface where every section is always
 * reachable.
 */
export interface WizardState {
  /** Data source configuration */
  dataSource: DataSourceConfig;
  /** Fields configuration */
  fields: FieldsConfig;
  /** Customization configuration */
  customize: CustomizeConfig;
}

/**
 * Default initial wizard state
 */
export const DEFAULT_WIZARD_STATE: WizardState = {
  dataSource: {
    primaryObjectType: null,
    relatedObjectTypes: [],
  },
  fields: {
    measures: [],
    groupBy: [],
  },
  customize: {
    filters: [],
    chartType: '',
    orderBy: null,
    limit: null,
  },
};

export {
  isValidFilterConditionLike,
  isValidTemplateWizardState,
  isValidWizardState,
  validateWizardStateFields,
} from './wizardStateValidation';
